// The Meta checkpoint, as every surface that shows it reads it (ORP-26180/26183): the app banner,
// the Meta Authentication card, the Health Checks card, and the "connection lost" banner that
// steps aside for it. One composable so the four cannot disagree about whether it is showing.
import { computed, shallowRef, type ComputedRef, type ShallowRef } from 'vue'

import { metaCheckpointVerifyPath } from '../api/ConnectionLifecycleApi'
import {
  canSeeMetaCheckpoint,
  FACEBOOK_URL,
  isMetaCheckpointActive,
  metaCheckpointIdentity,
  verifyMetaCheckpoint,
  type MetaCheckpointVerifyOutcome,
} from '../service/metaCheckpoint'

import { useAccount, useRbac, useWorkspace } from '@/features/account'

export interface UseMetaCheckpoint {
  /** This user may see the checkpoint UI at all — Health Checks filters its card on it. */
  canSee: ComputedRef<boolean>
  /** The checkpoint is active AND this user may see the checkpoint UI. Every surface gates on it. */
  visible: ComputedRef<boolean>
  /** The blocked Facebook profile, or `null` for the generic wording. */
  identity: ComputedRef<string | null>
  /** Verification is only actionable once the selected workspace is known. */
  canVerify: ComputedRef<boolean>
  verifying: ShallowRef<boolean>
  /**
   * "I've completed it". `path` is the Health Checks card's own `action_url`; the banner leaves it
   * out. A cleared block — or one that turned out to be a dead token — re-reads `account_detail`,
   * which then tells a different story (no banner, or the normal reconnect line).
   */
  verify: (path?: string) => Promise<MetaCheckpointVerifyOutcome>
  openFacebook: () => void
}

export function useMetaCheckpoint(): UseMetaCheckpoint {
  const account = useAccount()
  const rbac = useRbac()
  const workspace = useWorkspace()
  const verifying = shallowRef(false)

  const detail = computed(() => account.accountDetail.value as Record<string, unknown> | null)
  const canSee = computed(() => canSeeMetaCheckpoint(rbac.isSysAdmin.value))
  const canVerify = computed(() => workspace.workspaceId.value !== undefined)

  async function verify(path?: string): Promise<MetaCheckpointVerifyOutcome> {
    const workspaceId = workspace.workspaceId.value
    const target =
      path ?? (workspaceId === undefined ? null : metaCheckpointVerifyPath(workspaceId))
    if (verifying.value || target === null) return { status: 'canceled' }
    verifying.value = true
    try {
      const outcome = await verifyMetaCheckpoint(target)
      if (outcome.status === 'cleared' || outcome.status === 'reauth') {
        await account.loadSelectedAccountContext(true)
      }
      return outcome
    } finally {
      verifying.value = false
    }
  }

  return {
    canSee,
    visible: computed(() => canSee.value && isMetaCheckpointActive(detail.value)),
    identity: computed(() => metaCheckpointIdentity(detail.value)),
    canVerify,
    verifying,
    verify,
    openFacebook: () => {
      window.open(FACEBOOK_URL, '_blank', 'noopener,noreferrer')
    },
  }
}
